import Data from 'models/DataModel';
import { BrowserCache } from './browserCache';

export interface OrderItemDraftCacheIdentity {
  userId: string;
  orderItemId: string;
  questionSetVersion: string;
}

export interface OrderItemDraftDirtyFields {
  answers: Record<string, boolean>;
  tokens: Record<string, Record<string, boolean>>;
  address: boolean;
  attachments: boolean;
}

export interface OrderItemDraftCacheInput {
  baseETag: string | null;
  dirtyFields: OrderItemDraftDirtyFields;
  draftData: Data;
}

export interface PersistedOrderItemDraft extends OrderItemDraftCacheInput {
  timestamp: string;
}

const DraftCachePrefix = 'order-item-draft';
const DraftLifetimeMins = 60 * 24 * 7;

const encodeKeyPart = (value: string): string => encodeURIComponent(value);

export const getOrderItemDraftCacheKey = ({ userId, orderItemId, questionSetVersion }: OrderItemDraftCacheIdentity): string =>
  [DraftCachePrefix, userId, orderItemId, questionSetVersion].map(encodeKeyPart).join(':');

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const hasBooleanValues = (value: Record<string, unknown>): boolean =>
  Object.values(value).every((dirtyField) => typeof dirtyField === 'boolean');

const hasTokenDirtyFields = (value: Record<string, unknown>): boolean =>
  Object.values(value).every((dirtyTokens) => isObject(dirtyTokens) && hasBooleanValues(dirtyTokens));

const isOrderItemDraftDirtyFields = (value: unknown): value is OrderItemDraftDirtyFields => {
  if (!isObject(value)) {
    return false;
  }

  return (
    isObject(value.answers) &&
    hasBooleanValues(value.answers) &&
    isObject(value.tokens) &&
    hasTokenDirtyFields(value.tokens) &&
    typeof value.address === 'boolean' &&
    typeof value.attachments === 'boolean'
  );
};

const isPersistedOrderItemDraft = (value: unknown): value is PersistedOrderItemDraft => {
  if (!isObject(value)) {
    return false;
  }

  return (
    typeof value.timestamp === 'string' &&
    (typeof value.baseETag === 'string' || value.baseETag === null) &&
    isOrderItemDraftDirtyFields(value.dirtyFields) &&
    isObject(value.draftData)
  );
};

export const saveOrderItemDraft = (identity: OrderItemDraftCacheIdentity, draft: OrderItemDraftCacheInput): void => {
  const persistedDraft: PersistedOrderItemDraft = {
    timestamp: new Date().toISOString(),
    ...draft
  };

  BrowserCache.save(getOrderItemDraftCacheKey(identity), persistedDraft, DraftLifetimeMins);
};

export const loadOrderItemDraft = (identity: OrderItemDraftCacheIdentity): PersistedOrderItemDraft | null => {
  const key = getOrderItemDraftCacheKey(identity);
  const draft = BrowserCache.load<unknown>(key);

  if (!draft) {
    return null;
  }

  if (!isPersistedOrderItemDraft(draft)) {
    BrowserCache.clear(key);
    return null;
  }

  return draft;
};

export const clearOrderItemDraft = (identity: OrderItemDraftCacheIdentity): void => {
  BrowserCache.clear(getOrderItemDraftCacheKey(identity));
};
